import React, { useEffect, useState, useMemo } from "react";
import {
    Button,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import Select, { components } from "react-select";
import { Pencil } from "lucide-react";
import { useLoader } from "../../_helper/LoaderContext";
import { addPatient, updatePatient } from "../Patient/service/patient-service";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { stateMaster } from "../LabProfile/service/profile-service";
import S3ImageLoader from "../../S3ImageLoader";
import errorProfile from "../../assets/images/blank_profile.png";

// Custom MenuList with our custom slim scrollbar
const MenuList = (props) => (
    <components.MenuList
        {...props}
        className={`${props.className || ""} custom-slim-scrollbar`}
    >
        {props.children}
    </components.MenuList>
);

export default function AddPatient({
    toggleUploadModel,
    setUploadModelOpen,
    data = {},
    getList,
    getData,
    onPatientCreated,
}) {
    const [formData, setFormData] = useState({
        name: "",
        mobile: "",
        age: "",
        gender: "",
        email: "",
        address: "",
        state: "",
    });
    const { setLoading } = useLoader();
    const [errors, setErrors] = useState({});
    const [states, setStates] = useState([]);
    const [statesLoaded, setStatesLoaded] = useState(false);
    const [imageSource, setImageSource] = useState(null);
    const [imageSourceS3, setImageSourceS3] = useState(null);
    const [imageFile, setImageFile] = useState(null);

    const validate = () => {
        const newErrors = {};

        // Name validation — no numbers or special characters
        if (!formData.name?.trim()) {
            newErrors.name = "Name is required";
        } else if (!/^[A-Za-z\s]+$/.test(formData.name)) {
            newErrors.name = "Only letters and spaces allowed";
        }

        // Mobile validation — required
        if (!formData.mobile?.trim()) {
            newErrors.mobile = "Mobile number is required";
        }
        // if (!formData.state.trim()) {
        //     newErrors.state = "State is required";
        // }

        // Age validation
        if (!formData.age?.toString().trim()) {
            newErrors.age = "Age is required";
        } else if (isNaN(formData.age) || Number(formData.age) <= 0) {
            newErrors.age = "Enter valid age";
        }

        // Email validation
        if (!formData.email?.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Enter valid email";
        }

        // Gender validation
        if (!formData.gender) {
            newErrors.gender = "Gender is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const normalize = (v = "") => (typeof v === "string" ? v.trim().toLowerCase() : "");

    useEffect(() => {
        if (!data || states.length === 0) return;

        const matchedState = states.find(
            (s) => normalize(s.State) === normalize(data.state)
        );

        setFormData((prev) => ({
            ...prev,
            state: matchedState ? matchedState.State : prev.state || "",
        }));
    }, [data, states]);

    useEffect(() => {
        if (data && Object.keys(data).length > 0) {
            setFormData({
                name: data.name || "",
                mobile: data.contact ? String(data.contact) : "",
                age: data.age || "",
                gender: data.gender || "",
                email: data.email || "",
                address: data.address || "",
                state: data.state || "",
            });
            setImageSourceS3(data.profilePicture);
            setImageSource(null);
            setImageFile(null);
        } else {
            setFormData({
                name: "",
                mobile: "",
                age: "",
                gender: "",
                email: "",
                address: "",
                state: "",
            });
            setImageSourceS3(null);
            setImageSource(null);
            setImageFile(null);
        }
    }, [data]);

    useEffect(() => {
        fetchStates();
    }, []);

    const fetchStates = async () => {
        setLoading(true);
        try {
            const res = await stateMaster({ country: "India" });
            if (res.data.success === true) {
                setStates(res.data.data || []);
                setStatesLoaded(true);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/tiff",
            "image/bpg",
            "image/bmp",
            "image/heic",
            "image/heif",
        ];

        if (!allowedTypes.includes(file.type)) {
            toast.warning("Invalid image format");
            e.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setImageSource(reader.result); // ✅ local preview
        };
        reader.readAsDataURL(file);

        setImageFile(file);
    };

    // State options for react-select
    const stateOptions = useMemo(() => {
        return (states || []).map((s) => ({
            value: s.State,
            label: s.State,
        }));
    }, [states]);

    const selectedStateOption = useMemo(() => {
        if (!formData.state) return null;
        return (
            stateOptions.find(
                (opt) => normalize(opt.value) === normalize(formData.state)
            ) || {
                value: formData.state,
                label: formData.state,
            }
        );
    }, [formData.state, stateOptions]);

    const handleStateChange = (selected) => {
        setFormData((prev) => ({
            ...prev,
            state: selected ? selected.value : "",
        }));
        if (errors.state) {
            setErrors((prev) => ({ ...prev, state: null }));
        }
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (data?.name) {
            setLoading(true);
            try {
                const form_data = new FormData();
                form_data.append("fname", formData.name);
                form_data.append("email", formData.email);
                form_data.append("contact", formData.mobile);
                form_data.append("age", formData.age);
                form_data.append("gender", formData.gender);
                form_data.append("address", formData.address);
                form_data.append("state", formData.state);
                form_data.append("isWalkIn", true);
                form_data.append("profilePicture", imageFile);
                const res = await updatePatient(data._id, form_data);
                if (res.data.success === true) {
                    toast.success(res.data.message);
                    setFormData({
                        name: "",
                        mobile: "",
                        age: "",
                        gender: "",
                        email: "",
                        address: "",
                        state: "",
                    });
                    setImageFile(null);
                    setImageSource(null);
                    setErrors({});
                    if (setUploadModelOpen) setUploadModelOpen(false);
                    if (getList) getList();
                    if (getData) getData();
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        } else {
            if (validate()) {
                setLoading(true);
                try {
                    const form_data = new FormData();
                    form_data.append("fname", formData.name);
                    form_data.append("email", formData.email);
                    form_data.append("contact", formData.mobile);
                    form_data.append("age", formData.age);
                    form_data.append("gender", formData.gender);
                    form_data.append("address", formData.address);
                    form_data.append("state", formData.state);
                    form_data.append("isWalkIn", false);
                    form_data.append("profilePicture", imageFile);
                    const res = await addPatient(form_data);
                    if (res.data.success === true) {
                        toast.success(res.data.message);
                        setFormData({
                            name: "",
                            mobile: "",
                            age: "",
                            gender: "",
                            email: "",
                            address: "",
                            state: "",
                        });
                        setImageFile(null);
                        setImageSource(null);
                        setErrors({});
                        if (setUploadModelOpen) setUploadModelOpen(false);
                        if (onPatientCreated) onPatientCreated(res.data.data);
                        if (getList) getList();
                        if (getData) getData();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        }
    };

    // Styling for react-select state dropdown
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: "36px",
            height: "36px",
            fontSize: "13px",
            borderRadius: "4px",
            borderColor: errors.state
                ? "#dc3545"
                : state.isFocused
                ? "#1b255b"
                : "#ced4da",
            boxShadow: state.isFocused
                ? "0 0 0 2px rgba(27, 37, 91, 0.15)"
                : "none",
            "&:hover": {
                borderColor: errors.state ? "#dc3545" : "#1b255b",
            },
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: "36px",
            padding: "0 8px",
            fontSize: "13px",
        }),
        input: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
            fontSize: "13px",
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: "36px",
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: "4px 6px",
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: "4px 4px",
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            fontSize: "13px",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: "170px",
            padding: "4px 0",
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: "13px",
            padding: "6px 12px",
            backgroundColor: state.isSelected
                ? "#1b255b"
                : state.isFocused
                ? "rgba(27, 37, 91, 0.08)"
                : "transparent",
            color: state.isSelected ? "#ffffff" : "#212529",
            cursor: "pointer",
            ":active": {
                backgroundColor: "#1b255b",
                color: "#ffffff",
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d",
            fontSize: "13px",
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: "13px",
            color: "#212529",
        }),
    };

    return (
        <>
            {/* Compact Modal Header */}
            <div className="modal-header py-2 px-3 border-bottom d-flex align-items-center justify-content-between">
                <h5 className="modal-title fs-6 fw-semibold text-dark m-0">
                    {data?.name ? "Edit Patient" : "Add Patient"}
                </h5>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={toggleUploadModel}
                    style={{ fontSize: "0.75rem" }}
                />
            </div>

            {/* Compact Scrollable Modal Body */}
            <ModalBody
                className="px-3 py-2 custom-slim-scrollbar"
                style={{
                    maxHeight: "calc(85vh - 110px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >
                <Form onSubmit={handleSubmit}>
                    <div className="row gx-3 gy-2">
                        {/* Name */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    for="name"
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Name <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder="Enter name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    invalid={!!errors.name}
                                    style={{ height: "36px", fontSize: "13px" }}
                                />
                                {errors.name && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.name}
                                    </div>
                                )}
                            </FormGroup>
                        </div>

                        {/* Mobile Number */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    for="mobile"
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Mobile Number <span className="text-danger">*</span>
                                </Label>
                                <PhoneInput
                                    country={"in"}
                                    value={formData.mobile}
                                    onChange={(value) => {
                                        setFormData((prev) => ({ ...prev, mobile: value }));
                                        if (errors.mobile) {
                                            setErrors((prev) => ({ ...prev, mobile: null }));
                                        }
                                    }}
                                    inputProps={{
                                        name: "mobile",
                                        required: true,
                                        id: "mobile",
                                    }}
                                    containerStyle={{ width: "100%" }}
                                    inputStyle={{
                                        width: "100%",
                                        height: "36px",
                                        fontSize: "13px",
                                        borderRadius: "4px",
                                        borderColor: errors.mobile ? "#dc3545" : "#ced4da",
                                    }}
                                    buttonStyle={{
                                        borderRadius: "4px 0 0 4px",
                                        borderColor: errors.mobile ? "#dc3545" : "#ced4da",
                                        height: "36px",
                                    }}
                                    enableSearch={true}
                                    searchPlaceholder="Search country"
                                />
                                {errors.mobile && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.mobile}
                                    </div>
                                )}
                            </FormGroup>
                        </div>

                        {/* Age */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    for="age"
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Age <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="number"
                                    id="age"
                                    name="age"
                                    placeholder="Enter age"
                                    value={formData.age}
                                    onChange={handleChange}
                                    invalid={!!errors.age}
                                    style={{ height: "36px", fontSize: "13px" }}
                                />
                                {errors.age && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.age}
                                    </div>
                                )}
                            </FormGroup>
                        </div>

                        {/* Email (Placed first before Gender) */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    for="email"
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Email <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="Enter email address"
                                    value={formData.email}
                                    onChange={handleChange}
                                    invalid={!!errors.email}
                                    style={{ height: "36px", fontSize: "13px" }}
                                />
                                {errors.email && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.email}
                                    </div>
                                )}
                            </FormGroup>
                        </div>

                        {/* Gender (Placed below Email) */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Gender <span className="text-danger">*</span>
                                </Label>
                                <div
                                    className="d-flex align-items-center gap-4"
                                    style={{ height: "36px" }}
                                >
                                    <div className="radio radio-primary d-flex align-items-center m-0">
                                        <Input
                                            id="customer-radio-gloabal"
                                            type="radio"
                                            name="gender"
                                            value="male"
                                            checked={formData.gender === "male"}
                                            onChange={handleChange}
                                            className="me-1 mt-0"
                                        />
                                        <Label
                                            for="customer-radio-gloabal"
                                            className="mb-0 cursor-pointer"
                                            style={{ fontSize: "13px" }}
                                        >
                                            Male
                                        </Label>
                                    </div>
                                    <div className="radio radio-primary d-flex align-items-center m-0">
                                        <Input
                                            id="customer-radio-female"
                                            type="radio"
                                            name="gender"
                                            value="female"
                                            checked={formData.gender === "female"}
                                            onChange={handleChange}
                                            className="me-1 mt-0"
                                        />
                                        <Label
                                            for="customer-radio-female"
                                            className="mb-0 cursor-pointer"
                                            style={{ fontSize: "13px" }}
                                        >
                                            Female
                                        </Label>
                                    </div>
                                </div>
                                {errors.gender && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.gender}
                                    </div>
                                )}
                            </FormGroup>
                        </div>

                        {/* State (Searchable Select with custom slim scrollbar) */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    for="state-select-input"
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    State
                                </Label>
                                <Select
                                    id="state"
                                    name="state"
                                    inputId="state-select-input"
                                    value={selectedStateOption}
                                    onChange={handleStateChange}
                                    options={stateOptions}
                                    isSearchable={true}
                                    isClearable={true}
                                    placeholder="Select State"
                                    noOptionsMessage={() => "No states found"}
                                    components={{ MenuList }}
                                    styles={customSelectStyles}
                                    menuPortalTarget={
                                        typeof document !== "undefined"
                                            ? document.body
                                            : null
                                    }
                                    menuPosition="fixed"
                                />
                                {errors.state && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.state}
                                    </div>
                                )}
                            </FormGroup>
                        </div>

                        {/* Profile Image with interactive Avatar and Edit Icon */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Profile Image
                                </Label>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="position-relative d-inline-block">
                                        <input
                                            id="logo-upload"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                            className="d-none"
                                        />
                                        <label
                                            htmlFor="logo-upload"
                                            className="m-0 position-relative d-block cursor-pointer"
                                            style={{ cursor: "pointer" }}
                                            title="Click to upload profile photo"
                                        >
                                            <div
                                                className="rounded-circle border overflow-hidden d-flex align-items-center justify-content-center bg-light shadow-sm"
                                                style={{
                                                    width: "56px",
                                                    height: "56px",
                                                    border: "2px solid #e2e8f0",
                                                }}
                                            >
                                                {imageSource ? (
                                                    <img
                                                        src={imageSource}
                                                        alt="Profile Preview"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                        onError={(e) =>
                                                            (e.target.src = errorProfile)
                                                        }
                                                    />
                                                ) : imageSourceS3 ? (
                                                    <S3ImageLoader
                                                        fileName={imageSourceS3}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                        alt="Profile"
                                                        fallbackSrc={errorProfile}
                                                    />
                                                ) : (
                                                    <img
                                                        src={errorProfile}
                                                        alt="Default Profile"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            {/* Edit pencil icon badge */}
                                            <div
                                                className="position-absolute d-flex align-items-center justify-content-center rounded-circle bg-primary text-white shadow-sm"
                                                style={{
                                                    bottom: "0px",
                                                    right: "0px",
                                                    width: "20px",
                                                    height: "20px",
                                                    border: "2px solid #ffffff",
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                <Pencil size={10} strokeWidth={2.5} />
                                            </div>
                                        </label>
                                    </div>
                                    <div className="d-flex flex-column">
                                        <label
                                            htmlFor="logo-upload"
                                            className="m-0 fw-semibold text-primary cursor-pointer"
                                            style={{ fontSize: "12.5px", cursor: "pointer" }}
                                        >
                                            {imageSource || imageSourceS3
                                                ? "Change Photo"
                                                : "Upload Photo"}
                                        </label>
                                        <span className="text-muted" style={{ fontSize: "11px" }}>
                                            Click avatar to select
                                        </span>
                                    </div>
                                </div>
                            </FormGroup>
                        </div>

                        {/* Address (Textarea with 2 rows, compact) */}
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-2">
                                <Label
                                    for="address"
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    Address
                                </Label>
                                <Input
                                    type="textarea"
                                    id="address"
                                    name="address"
                                    placeholder="Enter address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    invalid={!!errors.address}
                                    rows={2}
                                    className="form-control form-control-sm"
                                    style={{
                                        height: "56px",
                                        minHeight: "56px",
                                        maxHeight: "72px",
                                        fontSize: "13px",
                                        resize: "none",
                                    }}
                                />
                                {errors.address && (
                                    <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {errors.address}
                                    </div>
                                )}
                            </FormGroup>
                        </div>
                    </div>
                </Form>
            </ModalBody>

            {/* Compact Modal Footer */}
            <ModalFooter className="py-2 px-3 border-top d-flex justify-content-end gap-2">
                <Button
                    color="secondary"
                    size="sm"
                    type="button"
                    onClick={toggleUploadModel}
                    className="px-3"
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    type="button"
                    onClick={handleSubmit}
                    className="px-3"
                >
                    {data?.name ? "Update" : "Submit"}
                </Button>
            </ModalFooter>
        </>
    );
}
